// ============================================================================
// c-design tokens — Variant C (Conversational / Guided) design system
//
// Mirrors the constants defined in the prototype shared.jsx + variant-c.jsx
// (claude.ai/design handoff, 2026-05-26). Single source of truth for
// colors, fonts, and shared sizing primitives across all c-design atoms.
//
// Naming follows the prototype verbatim so cross-referencing the original
// React code is trivial. Where the prototype used inline constants, we
// surface them here as named exports.
// ============================================================================

// ----- Brand & ink scale (from shared.jsx) -----
export const TAG_RED       = '#E63946';
export const TAG_RED_DEEP  = '#C82A3A';
export const INK_900       = '#0E1116';
export const INK_700       = '#2A2F37';
export const INK_500       = '#5B6270';
export const INK_400       = '#8A909A';
export const INK_300       = '#C6CAD1';
export const INK_200       = '#E4E6EA';
export const INK_100       = '#F2F3F5';
export const INK_50        = '#F8F9FA';
export const SURFACE       = '#FFFFFF';
export const GREEN         = '#1F8A5B';
export const AMBER         = '#B7791F';

// ----- Variant-C palette (from variant-c.jsx) -----
export const C_BG    = '#F5F1EA'; // page background (warm cream)
export const C_INK   = '#1F1B17'; // primary text on cream
export const C_INK2  = '#3D362E'; // secondary text on cream
export const C_MUTED = '#7A6E5F'; // tertiary text / labels
export const C_HAIR  = '#E5DED2'; // hairline border on cream
export const C_PANEL = '#FFFFFF'; // card surface
export const C_CREAM = '#FBF7F0'; // input bg on cream
export const C_RED   = TAG_RED;
export const C_GREEN = '#1F8A5B';

// ----- Type stack -----
// Heads + numbers prefer Inter Tight; numerics go to IBM Plex Mono.
// Loaded via Google Fonts <link> in the host Blade template.
export const FONT_DISPLAY = '"Inter Tight", Inter, sans-serif';
export const FONT_BODY    = 'Inter, sans-serif';
export const FONT_MONO    = '"IBM Plex Mono", monospace';

// ----- Formatters (also used by sale-history / commission rows) -----
export function fmt$(n) {
    if (n === null || n === undefined || Number.isNaN(n)) return '—';
    return '$' + Number(n).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

export function fmt$short(n) {
    if (!n) return '—';
    return '$' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

// ----- Mobile sticky bottom nav helper -----
// Returns a style object that, when isMobile=true, pins the wizard's
// Previous/Next nav to the bottom of the viewport with backdrop blur —
// matches the designer's MShell footer pattern from mobile-variants.jsx.
// Each component composes this with its own desktop layout.
//
// Usage:
//   navStyle() { return navMobileStickyStyle(this.isMobile, /*desktop=*/ {...}); }
//
// IMPORTANT: when sticky, the containing wrapStyle must add ≥100px of
// paddingBottom so the form content isn't hidden under the fixed bar.
export function navMobileStickyStyle(isMobile, desktopStyle) {
    if (!isMobile) return desktopStyle;
    return {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        padding: '12px 16px calc(env(safe-area-inset-bottom, 12px) + 12px)',
        background: 'rgba(245,241,234,0.94)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        borderTop: `1px solid ${C_HAIR}`,
        borderRadius: 0,
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        zIndex: 50,
    };
}
